export type Phase = 'pre-workshop' | 'workshop' | 'post-workshop';
export type StepKind =
  | 'profiling'
  | 'prepare'
  | 'propose'
  | 'workshop'
  | 'deepdive'
  | 'recommendation';

/** An authored external resource link, mirroring the portal's `HelpLink` shape (kept local so the
 * vendored frontier subtree stays decoupled from `support/campaigns`). */
export interface HelpLink {
  label: string;
  url: string;
}

/**
 * A generic step → campaign-asset file reference (from the portal's `metadata.files`). Names a `slot`
 * — the role the file plays for this step (e.g. `deck`) — and the asset it points to by `key`. Kept
 * local so the vendored subtree stays decoupled; structurally matches the portal's `RunnerFileRef`.
 */
export interface StepFileRef {
  slot: string;
  assetKey: string;
  kind?: string;
}

export interface StepDef {
  /** URL segment: /workshops/:workshopId/:slug */
  slug: string;
  kind: StepKind;
  phase: Phase;
  tag: string;
  title: string;
  /** Optional: a step whose heading says enough does not carry one. */
  subtitle?: string;
  /**
   * Authored help/resource links rendered in the step header (render-only-if-present). In the portal
   * these come from the campaign step's `metadata.helpLinks` via `resolveStepDef`; bundled steps carry
   * none (the old hard-coded placeholder `trainingVideoUrl`s were dropped when links became authored).
   */
  helpLinks?: HelpLink[];
  quickPrompts: string[];
  cta: string;
  /** Can be presented full-screen to the customer. */
  liveEligible: boolean;
  /**
   * Generic step → campaign-asset file references (from the portal step's `metadata.files`). Lets a
   * step declare which managed asset fills a named slot (e.g. `deck`) instead of the deck being
   * hard-coded; absent for bundled steps (they fall back to the hard-coded `DeckId`).
   */
  fileRefs?: StepFileRef[];
  /**
   * Generic step → predefined-question content-block references (from the portal step's
   * `metadata.questionRefs`). Lets a step declare WHICH question blocks it uses by (category, key)
   * instead of the runner reading a whole category blind; absent for bundled steps (they fall back to
   * the category read). Kept local so the vendored subtree stays decoupled.
   */
  questionRefs?: Array<{ slot?: string; category: string; key: string }>;
  /**
   * Propose only: the "Your conversation guide" block's heading and description. Authored via the
   * step's `metadata.runner.guideTitle` / `guideBody`; the runner falls back to its bundled copy
   * when unset. Kept on `StepDef` (optional) so the vendored subtree stays decoupled from the portal.
   */
  guideTitle?: string;
  guideBody?: string;
}

// The admin step-preview (`Admin/Campaigns/builder/StepPreview.tsx`) reads this to label a step's phase
// chip in the builder. The runner itself resolves phase labels from the seeded `CampaignPhase` rows
// (see `config/phases.ts`).
export const PHASE_LABEL: Record<Phase, string> = {
  'pre-workshop': 'Pre-Workshop',
  workshop: 'Workshop',
  'post-workshop': 'Post-Workshop',
};

export const STEPS: StepDef[] = [
  {
    slug: 'customer-profiling',
    kind: 'profiling',
    phase: 'pre-workshop',
    tag: 'Profiling',
    title: "Profile the Customer's Current Environment",
    subtitle:
      "Goal: This is where you build a clear picture of your customer's environment. Use Cisco Ready to pull their product category inventory, verify their readiness on Cisco so you walk into the next phase fully prepared.",
    quickPrompts: [
      'Where exactly do I get the install base export?',
      "I can't access Cisco Ready, what now?",
      "What's the difference between Ready and IQ?",
    ],
    cta: 'Continue to Prepare',
    liveEligible: false,
  },
  {
    slug: 'prepare',
    kind: 'prepare',
    phase: 'pre-workshop',
    tag: 'Prepare',
    title: 'Prepare the Pitch',
    subtitle:
      "Goal: This is where you get everything ready before your proposal meeting with the customer's champion.",
    quickPrompts: [
      'Who absolutely has to be in the room?',
      'How do I open with "why now"?',
      'Give me a better version of question 5',
    ],
    cta: 'Continue to Propose',
    liveEligible: false,
  },
  {
    slug: 'propose',
    kind: 'propose',
    phase: 'pre-workshop',
    tag: 'Propose',
    title: 'Kick Off the Proposal Meeting',
    subtitle:
      "Goal: This is your first meeting with the customer's champion. Present the pitch deck, walk them through the purpose of the workshop, and ask the discovery questions you picked earlier so you're fully prepared for workshop day.",
    quickPrompts: [
      'How should I open the proposal?',
      'They say they have no time. What do I say?',
      'How do I make the Ready/IQ findings land?',
    ],
    cta: 'Finish pre-workshop',
    // Nothing here is presented full-screen to the customer any more; the deck
    // itself is a plain download now, so this step has no live view.
    liveEligible: false,
  },
  {
    slug: 'workshop',
    kind: 'workshop',
    phase: 'workshop',
    tag: 'Workshop',
    title: 'Run the Workshop Session',
    subtitle:
      "Congratulations! Reaching this phase means you've successfully organized the workshop meeting, and your primary stakeholders are ready to join. This is where you present the agenda and walk through the workshop deck live, guiding the discussion as you go. Use the Cisco-based customized slides provided for this workshop rather than a generic deck.",
    quickPrompts: [
      'The customer wants to skip discovery, what do I say?',
      'How do I keep this on time?',
      'What\u2019s the strongest line for "why this workshop"?',
    ],
    cta: 'Continue to Deep-dive',
    liveEligible: true,
  },
  {
    slug: 'deep-dive',
    kind: 'deepdive',
    phase: 'workshop',
    tag: 'Deep-dive',
    title: 'Deep-dive: Guided Discovery',
    subtitle:
      'Goal: This is a collaborative, interactive session run together by the Account Executive and the Systems Engineer. Work through the deep-dive questions with each stakeholder in the room — a thorough session here is required to get a better report and recommendation out the other end.',
    quickPrompts: [
      'How do I get past a one-word answer?',
      'Which questions matter most if we are short on time?',
      'They do not know the answer. What now?',
    ],
    cta: 'Continue to Recommendation',
    // Collaborative in-room AE+SE Q&A session — there is no deck to present full-screen, so it is
    // not "Go Live" eligible (matches mike-frontier and the collaborative reframe above).
    liveEligible: false,
  },
  {
    slug: 'recommendation',
    kind: 'recommendation',
    phase: 'post-workshop',
    tag: 'Recommendation',
    title: 'Findings & Recommendation Report',
    subtitle:
      'This AI-generated report brings together everything you gathered from the proposal meeting and the workshop, so you can edit it and send it to your customer as the final deliverable.',
    quickPrompts: [
      'How do I prioritize these rows?',
      'Help me tighten the executive summary',
      'How do I present a row marked Not started?',
    ],
    cta: 'Finish workshop',
    // The recommendation report is a plain download/export deliverable now, not a
    // full-screen customer present.
    liveEligible: false,
  },
];

export function stepsForPhase(phase: Phase): StepDef[] {
  return STEPS.filter((s) => s.phase === phase);
}

export function stepIndexBySlug(slug: string | undefined): number {
  const i = STEPS.findIndex((s) => s.slug === slug);
  return i === -1 ? 0 : i;
}

export function firstSlugOfPhase(phase: Phase): string {
  return stepsForPhase(phase)[0].slug;
}
